import { SanitizedHtml } from '../components/SanitizedHtml';
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { talentyApi, type DomainLandingPageResponse } from '../services/talentyApi';

const DomainDetailPage: React.FC = () => {
  const { domain } = useParams<{ domain: string }>();
  const navigate = useNavigate();
  const [domainPage, setDomainPage] = useState<DomainLandingPageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDomainPage = async () => {
      if (!domain) {
        setError('Es wurde kein Bereich angegeben.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Use slug-based endpoint (domain param is now the slug)
        const response = await talentyApi.getDomainLandingPageBySlug(domain);
        
        // Console log the API response for debugging
        console.log('=== DOMAIN DETAIL API RESPONSE ===');
        console.log('Domain Slug:', domain);
        console.log('Full Response:', response);
        console.log('Title:', response.title);
        console.log('HTML Content Length:', response.html ? response.html.length : 0);
        console.log('Meta Description:', response.metaDescription);
        console.log('Keywords:', response.keywords);
        console.log('First 500 chars of HTML:', response.html ? response.html.substring(0, 500) : 'No HTML content');
        console.log('=== END DOMAIN DETAIL API RESPONSE ===');
        
        setDomainPage(response);
      } catch (err) {
        console.error('Bereichsseite konnte nicht geladen werden:', err);
        setError('Die Bereichsseite konnte nicht geladen werden.');
      } finally {
        setLoading(false);
      }
    };

    fetchDomainPage();
  }, [domain]);

  // Apply gradient backgrounds and add click handlers after content is loaded
  useEffect(() => {
    if (domainPage) {
      // Wait for DOM to update, then apply gradient backgrounds and click handlers
      setTimeout(() => {
        // Hide any video elements and apply gradient backgrounds
        const videos = document.querySelectorAll('.generated-content video, .hero-video, video');
        videos.forEach((video) => {
          (video as HTMLElement).style.display = 'none';
        });
        
        // Apply gradient to hero sections
        const heroSections = document.querySelectorAll('.generated-content .hero, .generated-content .hero-background');
        heroSections.forEach((hero) => {
          const heroEl = hero as HTMLElement;
          heroEl.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
          heroEl.style.minHeight = '500px';
        });
        
        // Add click handlers to family cards using API response data
        let familyCardsFound = 0;
        
        if (domainPage.familySlugs && domainPage.familySlugs.length > 0) {
          console.log('Available family slugs from API:', domainPage.familySlugs);
          
          // Look for card-like containers that contain family information
          const cardSelectors = [
            '.generated-content .card',
            '.generated-content .position-card',
            '.generated-content .family-card',
            '.generated-content div[class*="card"]',
            '.generated-content div[class*="position"]',
            '.generated-content div[class*="family"]',
            '.generated-content section',
            '.generated-content .row > div',
            '.generated-content .col > div',
          ];
          
          // Track processed elements to avoid duplicates
          const processedElements = new Set();
          
          cardSelectors.forEach(selector => {
            const cardContainers = document.querySelectorAll(selector);
            console.log(`Found ${cardContainers.length} potential card containers with selector: ${selector}`);
            
            cardContainers.forEach((container) => {
              const containerEl = container as HTMLElement;
              const containerText = containerEl.textContent?.trim() || '';
              
              // Skip if already processed or no text
              if (!containerText || processedElements.has(containerEl)) return;
              
              // Check if this container's text matches any family slug
              const matchingFamilySlug = domainPage.familySlugs?.find(slug => {
                const slugLower = slug.toLowerCase();
                const textLower = containerText.toLowerCase();
                const slugWithoutParens = slug.replace(/[()]/g, '').trim();
                
                return containerText.includes(slug) ||
                       containerText.includes(slugWithoutParens) ||
                       textLower.includes(slugLower) ||
                       // Try matching just the main part (before parentheses)
                       (slug.includes('(') && textLower.includes(slug.split('(')[0].trim().toLowerCase()));
              });
              
              if (matchingFamilySlug) {
                console.log(`✅ Found family card container:`, {
                  tagName: containerEl.tagName,
                  className: containerEl.className,
                  textPreview: containerText.substring(0, 100) + '...',
                  matchingFamilySlug: matchingFamilySlug
                });
                
                // Mark as processed
                processedElements.add(containerEl);
                containerEl.setAttribute('data-family-click-handler', 'true');
                
                // Add cursor pointer style
                containerEl.style.cursor = 'pointer';
                
                // Add click event listener to the entire card container
                containerEl.addEventListener('click', (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log(`🔗 Navigating to family: ${matchingFamilySlug}`);
                  navigate(`/family/${encodeURIComponent(matchingFamilySlug)}`);
                });
                
                // Add hover effect to the entire card
                containerEl.addEventListener('mouseenter', () => {
                  containerEl.style.transform = 'translateY(-2px)';
                  containerEl.style.transition = 'transform 0.2s ease';
                  containerEl.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                });
                
                containerEl.addEventListener('mouseleave', () => {
                  containerEl.style.transform = 'translateY(0)';
                  containerEl.style.boxShadow = '';
                });
                
                familyCardsFound++;
              }
            });
          });
          
          // If no cards found with specific selectors, try a more general approach
          if (familyCardsFound === 0) {
            console.log('No cards found with specific selectors, trying general approach...');
            
            const allDivs = document.querySelectorAll('.generated-content div');
            allDivs.forEach((div) => {
              const divEl = div as HTMLElement;
              const divText = divEl.textContent?.trim() || '';
              
              if (divText && !processedElements.has(divEl)) {
                const matchingFamilySlug = domainPage.familySlugs?.find(slug => {
                  const slugLower = slug.toLowerCase();
                  const textLower = divText.toLowerCase();
                  return divText.includes(slug) || textLower.includes(slugLower);
                });
                
                if (matchingFamilySlug) {
                  // Find the most appropriate parent container (not too big, not too small)
                  let targetElement = divEl;
                  let parent = divEl.parentElement;
                  
                  while (parent && parent.classList.contains('generated-content') === false) {
                    const parentText = parent.textContent?.trim() || '';
                    // If parent text is not much longer than current text, use parent
                    if (parentText.length < divText.length * 2 && parentText.length < 500) {
                      targetElement = parent;
                    }
                    parent = parent.parentElement;
                  }
                  
                  if (!processedElements.has(targetElement)) {
                    console.log(`✅ Found family element (general approach): "${targetElement.textContent?.substring(0, 100)}..." -> "${matchingFamilySlug}"`);
                    
                    processedElements.add(targetElement);
                    targetElement.setAttribute('data-family-click-handler', 'true');
                    targetElement.style.cursor = 'pointer';
                    
                    targetElement.addEventListener('click', (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate(`/family/${encodeURIComponent(matchingFamilySlug)}`);
                    });
                    
                    targetElement.addEventListener('mouseenter', () => {
                      targetElement.style.transform = 'translateY(-2px)';
                      targetElement.style.transition = 'transform 0.2s ease';
                      targetElement.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                    });
                    
                    targetElement.addEventListener('mouseleave', () => {
                      targetElement.style.transform = 'translateY(0)';
                      targetElement.style.boxShadow = '';
                    });
                    
                    familyCardsFound++;
                  }
                }
              }
            });
          }
        } else {
          console.log('No family slugs available from API response');
        }

        // Add click handlers to job cards (job navigation by slug)
        const jobCards = document.querySelectorAll('.generated-content .position-card[data-job-slug], .generated-content .job-card[data-job-slug]');
        jobCards.forEach((card) => {
          const cardEl = card as HTMLElement;
          const jobSlug = cardEl.getAttribute('data-job-slug');
          
          if (jobSlug) {
            // Add cursor pointer style
            cardEl.style.cursor = 'pointer';
            
            // Add click event listener
            cardEl.addEventListener('click', () => {
              navigate(`/job/${jobSlug}`);
            });
            
            // Add hover effect
            cardEl.addEventListener('mouseenter', () => {
              cardEl.style.transform = 'translateY(-2px)';
              cardEl.style.transition = 'transform 0.2s ease';
            });
            
            cardEl.addEventListener('mouseleave', () => {
              cardEl.style.transform = 'translateY(0)';
            });
          }
        });

        // Fix existing job links that might use titles instead of slugs
        const jobLinks = document.querySelectorAll('.generated-content a[href^="/job/"]');
        jobLinks.forEach((link) => {
          const linkEl = link as HTMLAnchorElement;
          const href = linkEl.getAttribute('href');
          
          if (href) {
            // Check if the href contains encoded characters (likely a title)
            if (href.includes('%20') || href.includes(' ')) {
              // This looks like a title, prevent default click and log warning
              linkEl.addEventListener('click', (e) => {
                e.preventDefault();
                console.warn('Job link uses title instead of slug:', href);
                console.warn('Please ensure job links use data-job-slug attributes with proper slugs');
              });
              
              // Add visual indicator that this link is broken
              linkEl.style.color = '#dc3545';
              linkEl.style.textDecoration = 'line-through';
              linkEl.title = 'Dieser Link ist fehlerhaft und wurde deshalb deaktiviert.';
            }
          }
        });
        
        console.log(`Applied gradient backgrounds to ${heroSections.length} hero sections`);
        console.log(`Added click handlers to ${familyCardsFound} family cards`);
        console.log(`Added click handlers to ${jobCards.length} job cards`);
        console.log(`Fixed ${jobLinks.length} job links`);
      }, 100);
    }
  }, [domainPage, navigate]);

  // Set document title and meta description when domain page loads
  useEffect(() => {
    if (domainPage) {
      document.title = domainPage.title;
      
      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', domainPage.metaDescription);
      } else {
        const meta = document.createElement('meta');
        meta.name = 'description';
        meta.content = domainPage.metaDescription;
        document.head.appendChild(meta);
      }

      // Update meta keywords
      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute('content', domainPage.keywords.join(', '));
      } else {
        const meta = document.createElement('meta');
        meta.name = 'keywords';
        meta.content = domainPage.keywords.join(', ');
        document.head.appendChild(meta);
      }
    }
  }, [domainPage]);

  if (loading) {
    return (
      <div className="job-detail-page">
        <div className="container">
          <div className="loading">Lade Bereich...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="job-detail-page">
        <div className="container">
          <div className="error">
            <h2>Bereich nicht gefunden</h2>
            <p>{error}</p>
            <Link to="/domains" className="btn btn-primary">
              Zurück zu allen Bereichen
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!domainPage) {
    return (
      <div className="job-detail-page">
        <div className="container">
          <div className="error">
            <h2>Bereich nicht verfügbar</h2>
            <Link to="/domains" className="btn btn-primary">
              Zurück zu allen Bereichen
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="job-detail-page">
      <div className="breadcrumb">
        <div className="container">
          <Link to="/domains" className="breadcrumb-link">Alle Bereiche</Link>
          <span className="breadcrumb-separator">›</span>
          <span className="breadcrumb-current">{domainPage.title}</span>
        </div>
      </div>
      
      <div className="job-content">
        {/* Render the generated HTML from Talenty */}
        <SanitizedHtml className="generated-content" content={domainPage.html} format="generated" />
      </div>
    </div>
  );
};

export default DomainDetailPage;
